import { AuthDirectLoginWithVerificationCodeQuery, UserId } from '@cibo/core'
import { SubmitButton } from '@cibo/ui'
import CheckIcon from '@mui/icons-material/CheckRounded'
import RefreshRounded from '@mui/icons-material/RefreshRounded'
import { Button, FormHelperText, InputLabel, Stack, Typography } from '@mui/material'
import { Field, Form, Formik } from 'formik'
import { TextField } from 'formik-mui'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'
import * as Yup from 'yup'
import { AUTH_ROUTES } from '../../pages/consts'
import {
  hasLowerCaseCheck,
  hasMinLengthCheck,
  hasNumberCheck,
  hasSymbolCheck,
  hasUpperCaseCheck,
} from '../../utils'

export interface CreateNewPasswordFormValues
  extends Pick<AuthDirectLoginWithVerificationCodeQuery, 'verifyCode' | 'password' | 'password2'> {}

interface CreateNewPasswordFormProps
  extends Partial<Pick<AuthDirectLoginWithVerificationCodeQuery, 'verifyCode'>> {
  onSubmit: (values: CreateNewPasswordFormValues) => void
  onResendCode: () => void
  isLoading: boolean
  error: any
  userId?: UserId
}

export const CreateNewPasswordForm = ({
  error,
  isLoading,
  onSubmit,
  onResendCode,
  verifyCode,
}: CreateNewPasswordFormProps) => {
  const { t } = useTranslation('auth')

  const [hasNumber, setHasNumber] = useState(false)
  const [hasSymbol, setHasSymbol] = useState(false)
  const [hasUpperCase, setHasUpperCase] = useState(false)
  const [hasLowerCase, setHasLowerCase] = useState(false)
  const [hasMinLength, setHasMinLength] = useState(false)

  /**
   * There is probably a better way to do this.
   * The validation schema never changes, but we need access to the react-i18next
   * context to localize the error messages; hence this ephemeral validation schema
   */
  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        verifyCode: Yup.string().required(t('verifyCodeRequired')),
        password: Yup.string()
          .required(t('passwordErrorRequired'))
          .min(8, t('passwordErrorTooShort'))
          .max(128, t('passwordErrorTooLong'))
          .test('password-quality', '', (password = '') => {
            const hasNumber = hasNumberCheck(password)
            setHasNumber(!!hasNumber)
            const hasSymbol = hasSymbolCheck(password)
            setHasSymbol(!!hasSymbol)
            const hasLowerCase = hasLowerCaseCheck(password)
            setHasLowerCase(!!hasLowerCase)
            const hasUpperCase = hasUpperCaseCheck(password)
            setHasUpperCase(!!hasUpperCase)
            const hasMinLength = hasMinLengthCheck(password)
            setHasMinLength(!!hasMinLength)
            return !!hasSymbol || !!hasNumber || !!hasLowerCase || !!hasUpperCase || !!hasMinLength
          }),
        password2: Yup.string().test(
          'do-passwords-match',
          t('confirmPasswordErrorMismatch'),
          function (password2) {
            return this.parent.password === password2
          }
        ),
      }),
    []
  )

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={{
        verifyCode,
        password: '',
        password2: '',
      }}
      onSubmit={({ verifyCode, ...rest }) =>
        //sanitize the input
        onSubmit({ verifyCode: verifyCode ? verifyCode.trim().toUpperCase() : '', ...rest })
      }
    >
      {({ setFieldValue }) => (
        <Form>
          <Stack spacing={2}>
            <Stack>
              <InputLabel shrink htmlFor="verifyCode">
                {t('verifyCode')}
              </InputLabel>
              <Field
                id="verifyCode"
                name="verifyCode"
                component={TextField}
                InputProps={{
                  inputProps: { autoComplete: 'off', style: { textTransform: 'uppercase' } },
                }}
                disabled={false}
              />
            </Stack>

            <Stack>
              <Typography>{t('passwordRules')}</Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CheckIcon sx={{ visibility: hasNumber ? '' : 'hidden' }} color="success" />
                <Typography variant="body2">{t('needsNumber')}</Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CheckIcon sx={{ visibility: hasSymbol ? '' : 'hidden' }} color="success" />
                <Typography variant="body2">{t('needsSymbol')}</Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CheckIcon sx={{ visibility: hasUpperCase ? '' : 'hidden' }} color="success" />
                <Typography variant="body2">{t('needsUpperCase')}</Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CheckIcon sx={{ visibility: hasLowerCase ? '' : 'hidden' }} color="success" />
                <Typography variant="body2">{t('needsLowerCase')}</Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CheckIcon sx={{ visibility: hasMinLength ? '' : 'hidden' }} color="success" />
                <Typography variant="body2">{t('passwordErrorTooShort')}</Typography>
              </Stack>
            </Stack>

            <Stack>
              <InputLabel shrink htmlFor="password">
                {t('password')}
              </InputLabel>
              <Field
                id="password"
                name="password"
                component={TextField}
                InputProps={{ inputProps: { autoComplete: 'new-password' } }}
                type="password"
                disabled={isLoading}
              />
            </Stack>
            <Stack>
              <InputLabel shrink htmlFor="password2">
                {t('confirmPassword')}
              </InputLabel>
              <Field
                id="password2"
                name="password2"
                component={TextField}
                InputProps={{ inputProps: { autoComplete: 'new-password' } }}
                type="password"
                disabled={isLoading}
              />
            </Stack>

            {error && <FormHelperText error>{t('updatePasswordError')}</FormHelperText>}
            <SubmitButton disabled={isLoading} title={t('updatePassword')} />
            <Stack direction="row" justifyContent={'space-between'}>
              <Button component={NavLink} to={AUTH_ROUTES.LOGIN}>
                {t('cancel')}
              </Button>
              <Button
                startIcon={<RefreshRounded />}
                onClick={() => {
                  //clear the code since it will be stale
                  setFieldValue('verifyCode', '')
                  onResendCode()
                }}
                disabled={isLoading}
              >
                {t('resendCode')}
              </Button>
            </Stack>
          </Stack>
        </Form>
      )}
    </Formik>
  )
}
